// AGC: Expenses API Service
// AGC: Handles all API calls related to expenses
// AGC: All expenses are filtered by user account - each user only sees their own expenses

import { getBackendUrl } from '../utils/backendUrl'
import type { Expense } from '../types'

// AGC: Backend URL for API calls
const BASE_URL = getBackendUrl()

/**
 * AGC: Get all expenses from backend for the authenticated user
 * AGC: Requires authentication token - expenses are filtered by userId
 */
export const getExpenses = async (authToken?: string | null): Promise<Expense[]> => {
  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }

    // AGC: Add authentication token if provided
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }

    const response = await fetch(`${BASE_URL}/api/v1/expenses`, {
      method: 'GET',
      headers,
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication required. Please log in again.')
      }
      throw new Error(`Failed to fetch expenses: ${response.statusText}`)
    }

    const data = await response.json()
    return data.expenses || []
  } catch (error) {
    console.error('AGC: Error fetching expenses:', error)
    // AGC: Return empty array on error (graceful degradation)
    return []
  }
}

/**
 * AGC: Create a new expense for the authenticated user
 * AGC: Requires authentication token - expense is associated with userId
 */
export const createExpense = async (
  expenseData: {
    title: string
    value: number
    date: string
    category?: string
    notes?: string
  },
  authToken?: string | null
): Promise<Expense> => {
  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }

    // AGC: Add authentication token if provided
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }

    const response = await fetch(`${BASE_URL}/api/v1/expenses`, {
      method: 'POST',
      headers,
      body: JSON.stringify(expenseData),
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication required. Please log in again.')
      }
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `Failed to create expense: ${response.statusText}`)
    }

    const data = await response.json()
    return data.expense
  } catch (error) {
    console.error('AGC: Error creating expense:', error)
    throw error
  }
}

/**
 * AGC: Delete an expense
 * AGC: Accepts optional auth token for authenticated deletion
 */
export const deleteExpense = async (expenseId: string, authToken?: string | null): Promise<void> => {
  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }

    // AGC: Add authentication token if provided
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }

    const response = await fetch(`${BASE_URL}/api/v1/expenses/${expenseId}`, {
      method: 'DELETE',
      headers,
    })

    if (!response.ok) {
      throw new Error(`Failed to delete expense: ${response.statusText}`)
    }
  } catch (error) {
    console.error('AGC: Error deleting expense:', error)
    throw error
  }
}

